import { z } from 'zod';

/**
 * Tipos de documento aceptados en Colombia.
 * CC = Cédula de ciudadanía, CE = Cédula de extranjería,
 * TI = Tarjeta de identidad, RC = Registro civil, PA = Pasaporte.
 */
export const DocumentTypeSchema = z.enum(['CC', 'CE', 'TI', 'RC', 'PA']);
export type DocumentType = z.infer<typeof DocumentTypeSchema>;

export const SexSchema = z.enum(['F', 'M', 'O']);
export type Sex = z.infer<typeof SexSchema>;

/**
 * Estado de un documento clínico con preguardado: `borrador` (llenado en curso,
 * editable, sin firma) o `firmado` (final). Lo usan el registro de anestesia y
 * la hoja de gasto.
 */
export const DraftStatusSchema = z.enum(['borrador', 'firmado']);
export type DraftStatus = z.infer<typeof DraftStatusSchema>;

/**
 * Identificadores tipados — usar en lugar de string crudo
 * para evitar mezclar IDs de diferentes agregados.
 */
export const PatientIdSchema = z.string().regex(/^P-\d{5,}$/, 'Formato de id de paciente inválido');
export const ProcedureIdSchema = z
  .string()
  .regex(/^EP-\d{4}-\d{5,}$/, 'Formato de id de episodio inválido');
export const UserIdSchema = z.string().regex(/^U-\d{3,}$/, 'Formato de id de usuario inválido');

/**
 * Id de rol/perfil (slug). Los roles ya no son un enum fijo: son datos, así que
 * `users.rol` puede ser un slug built-in (`admin`, `medico`, …) o uno de un
 * perfil personalizado. Vive en `common` para evitar ciclos entre users/roles.
 */
export const RoleIdSchema = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z][a-z0-9-]*$/, 'Slug inválido (minúsculas, dígitos y guiones)');

export type PatientId = z.infer<typeof PatientIdSchema>;
export type ProcedureId = z.infer<typeof ProcedureIdSchema>;
export type UserId = z.infer<typeof UserIdSchema>;
export type RoleId = z.infer<typeof RoleIdSchema>;

/** Paginación estándar para endpoints de listado. */
export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  q: z.string().trim().min(1).max(120).optional(),
  sort: z.string().regex(/^[a-zA-Z_]+:(asc|desc)$/).optional(),
});

export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export const PaginatedResultSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
  });

/**
 * Validador reusable para firmas en formato dataURL PNG o JPEG (base64).
 * Acepta solo `image/png`, `image/jpeg`, `image/jpg`. Tamaño 50 chars (validez
 * mínima del header + algo de cuerpo) a 200 KB (límite razonable para PNG
 * de firma — un trazo en canvas 600x300 raramente supera 30 KB).
 *
 * Usado por todas las firmas digitales del dominio (consentimiento, anestesia,
 * hoja de gasto). Protege al backend frente a clientes que envíen contenidos
 * arbitrarios — por ejemplo un atacante que intente colar un SVG con script.
 */
export const SignatureDataUrlSchema = z
  .string()
  .min(50)
  .max(200_000)
  .regex(
    /^data:image\/(png|jpe?g);base64,[A-Za-z0-9+/=]+$/,
    'Firma debe ser un dataURL PNG o JPEG en base64',
  );

/** Telemetría de auditoría que acompaña a comandos sensibles. */
export const AuditContextSchema = z.object({
  ip: z.string().ip().optional(),
  userAgent: z.string().max(500).optional(),
  deviceId: z.string().max(120).optional(),
});
export type AuditContext = z.infer<typeof AuditContextSchema>;

/** Respuesta de error normalizada (RFC 7807 simplificado). */
export const ApiErrorSchema = z.object({
  type: z.string().url().or(z.literal('about:blank')),
  title: z.string(),
  status: z.number().int().min(400).max(599),
  detail: z.string().optional(),
  instance: z.string().optional(),
  errors: z.record(z.array(z.string())).optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
